/**
 * QA switches for the mock source, read from the page URL at call time:
 *
 *   ?latency=0            simulated latency in ms (default 1200 — the prototype's settle)
 *   ?mock-error=nps,bootstrap   these endpoints fail (a widget id, or a method name)
 *   ?mock-empty=nps       these widgets answer as if every chip were emptied
 *
 * Several ids are comma-separated; `*` matches every endpoint.
 */
export const DEFAULT_LATENCY_MS = 1200;

function params(): URLSearchParams {
  return typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
}

function listHas(name: string, endpoint: string): boolean {
  const raw = params().get(name);
  if (!raw) return false;
  const ids = raw.split(',').map((s) => s.trim());
  return ids.includes('*') || ids.includes(endpoint);
}

export function latencyMs(): number {
  const raw = params().get('latency');
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_LATENCY_MS;
}

export const shouldFail = (endpoint: string) => listHas('mock-error', endpoint);
export const shouldBeEmpty = (endpoint: string) => listHas('mock-empty', endpoint);

export class MockScenarioError extends Error {
  constructor(endpoint: string) {
    super(`Mock scenario: "${endpoint}" was asked to fail (?mock-error).`);
    this.name = 'MockScenarioError';
  }
}

export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/** Latency, then the error switch. Every mock endpoint goes through this. */
export async function simulate(
  endpoint: string,
  signal?: AbortSignal,
  options: { latency?: boolean } = {},
): Promise<void> {
  if (options.latency !== false) await delay(latencyMs(), signal);
  if (shouldFail(endpoint)) throw new MockScenarioError(endpoint);
}
